
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "@/lib/toast";
import { supabase } from "@/integrations/supabase/client";
import { Link } from "react-router-dom";
import { AlertCircle, CheckCircle } from "lucide-react";

export default function ResetPassword() {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [isSuccess, setIsSuccess] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg("");
    setMessage("");
    setIsSuccess(false);
    
    if (!email) {
      setErrorMsg("Por favor, informe seu email");
      toast.error("Por favor, informe seu email");
      return;
    }
    
    try {
      setIsLoading(true);
      
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/update-password`,
      });
      
      if (error) throw error;
      
      setIsSuccess(true);
      setMessage("Email de redefinição de senha enviado. Verifique sua caixa de entrada.");
      toast.success("Email de redefinição de senha enviado");
    } catch (error: any) {
      console.error("Erro ao enviar email de redefinição:", error);
      let errorMessage = "Erro ao solicitar redefinição de senha";
      
      if (error.message) {
        errorMessage = error.message;
      }
      
      setErrorMsg(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 px-4">
      <div className="mb-8 text-center">
        <h1 className="text-3xl font-bold text-primary">Minha Agência</h1>
        <p className="text-gray-600 mt-2">
          Redefinir sua senha
        </p>
      </div>
      
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Esqueceu sua senha?</CardTitle>
          <CardDescription>
            Digite seu email e enviaremos instruções para redefinir sua senha
          </CardDescription>
        </CardHeader>
        <form onSubmit={handleSubmit}>
          <CardContent className="space-y-4">
            {errorMsg && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-md flex items-start text-sm text-red-800 dark:bg-red-950/30 dark:border-red-900 dark:text-red-300">
                <AlertCircle className="h-4 w-4 mr-2 mt-0.5 text-red-600 dark:text-red-400" />
                <span>{errorMsg}</span>
              </div>
            )}
            
            {isSuccess && message && (
              <div className="p-3 bg-green-50 border border-green-200 rounded-md flex items-start text-sm text-green-800 dark:bg-green-950/30 dark:border-green-900 dark:text-green-300">
                <CheckCircle className="h-4 w-4 mr-2 mt-0.5 text-green-600 dark:text-green-400" />
                <span>{message}</span>
              </div>
            )}
            
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input 
                id="email" 
                type="email" 
                placeholder="seu@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isSuccess}
                required
              />
            </div>
          </CardContent>
          <CardFooter className="flex flex-col space-y-4">
            <Button 
              type="submit" 
              className="w-full bg-blue-600 hover:bg-blue-700"
              disabled={isLoading || isSuccess}
            >
              {isLoading ? "Enviando..." : "Enviar instruções"}
            </Button>
            <div className="text-center text-sm">
              Lembrou a senha?{" "}
              <Link 
                to="/login" 
                className="text-primary hover:underline"
              >
                Voltar ao login
              </Link>
            </div>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
}
